import type { BgSpot, BgView } from "@bing/engine-backgammon";
import {
  applySubMove,
  boardFromView,
  countAt,
  ownerAt,
  pipToOff,
  setupBoard,
  type Board,
} from "@bing/engine-backgammon";

/* ------------------------------------------------------------------ *
 * تخته نرد — the REPLAY layer (pure half).
 *
 * The board must not render the authoritative view directly: frames
 * arrive as fast as the mover taps, so a real opponent's checkers
 * teleport (and the table's single flight slot cancelled its own
 * predecessor mid-air). This module turns a `rendered → incoming` view
 * pair into an ORDERED event list and says how long each event takes.
 *
 * IT NEVER DIFFS POSITIONS TO GUESS A MOVE. `staged` is a prefix-stable
 * ordered list — `legalNextSubMoves` only offers a sub-move whose prefix
 * equals `staged`, and `undo` pops the tail — so the un-shown sub-moves
 * are exactly `incoming.staged.slice(shown)`, in order. On confirm,
 * `lastMove` carries the whole completed turn, so a client that missed
 * frames (a reconnect, a batched frame, joining mid-turn) still replays
 * the exact turn. A position diff could recover neither the ORDER nor the
 * die attribution, so it would be inventing information.
 *
 * Rule questions it CAN answer ("was that a hit?") are answered against
 * the board we are CURRENTLY SHOWING, using the same primitives the engine
 * uses (`ownerAt`/`countAt`), so they cannot drift from the rules. Anything
 * that depends on the RULESET rather than the position — §5.2's frozen
 * hitter is the one that bit us — belongs on the view, not here.
 * ------------------------------------------------------------------ */

/* ── The client half of the pacing contract ──────────────────────────────────────────────────
   LOCKSTEP with packages/engines/backgammon/src/engine.ts:
     THROW_MS + ROLL_HOLD_MS === ROLL_READ_MS (2600)
     STEP_MS                 === postMoveDelayMs's between-sub-moves value (950)
   Change one side and a bot's turn and a person's turn stop feeling alike. ─────────────────── */

/** The die's throw duration. Mirrored from game/backgammon-dice so this module imports no DOM. */
export const THROW_MS = 1000;
/** How long a LANDED roll sits before anything moves — the window to actually read the numbers. */
export const ROLL_HOLD_MS = 1600;
/** The gap between one sub-move and the next. */
export const STEP_MS = 950;
/** One checker's walk. */
export const WALK_MS = 700;
/** The knocked blot's flight to the bar. */
export const HIT_MS = 500;
/** The tray slot lighting as a checker comes off. */
export const BEAROFF_SETTLE_MS = 300;
/** The turn changing hands. */
export const HANDOVER_MS = 500;
/** The §5.1 opening roll-off ceremony, once per game. */
export const OPENING_MS = 3000;

/**
 * Beyond this many un-shown sub-moves the tempo compresses. Reachable by a reconnect or a batched
 * frame — NOT by ordinary tapping, which is why normal play never speeds up. `motionMs` is never
 * compressed: a checker always visibly walks.
 */
export const MAX_BACKLOG = 3;
export const COMPRESS_FACTOR = 0.4;
export const COMPRESS_FLOOR_MS = 250;

/** One sub-move as the view reports it (the view carries no die). */
export interface BgSub {
  readonly from: BgSpot;
  readonly to: BgSpot;
}

export type BgEvent =
  | { kind: "openingRollOff"; dice: readonly [number, number]; winner: number }
  | { kind: "roll"; dice: readonly number[]; roller: number; isDoubles: boolean }
  /**
   * One checker walking. A hit and a bear-off are CONSEQUENCES of a walk rather than independent
   * happenings, so they ride on it: one thing to time, to test and to animate.
   *
   * ⚠️ A `freeze` flag used to ride here too, computed as «was it a hit, and did it land in the
   * mover's own home?». That is GEOMETRY, not the rule: it was true on a double (where §5.2 is
   * inactive for the whole turn), true when the no-other-move exception had lifted the ban, and true
   * with no dice left to withhold anything — and, being per-mover, it raised the notice on a viewer's
   * screen while the OPPONENT hit inside THEIR home. The board now reads `view.frozen`, which is the
   * engine's own answer. Do not reintroduce a client-side guess.
   */
  | {
      kind: "walk";
      sub: BgSub;
      mover: number;
      /** The point a blot was knocked off (and sent to the bar), else null. */
      hit: number | null;
      bearOff: boolean;
    }
  | {
      kind: "undo";
      sub: BgSub;
      mover: number;
      /** The point a knocked blot returns to, else null. */
      unhit: number | null;
    }
  | { kind: "dance"; seat: number }
  | { kind: "handover"; to: number };

const other = (seat: 0 | 1): 0 | 1 => (seat === 0 ? 1 : 0);
const seatOf = (n: number): 0 | 1 => (n === 0 ? 0 : 1);

/**
 * Which die a sub-move consumed. Exact for board→board and for a bar entry; for a bear-off this is
 * the EXACT pip, which an overshoot may not equal — fine for display, and never used to APPLY a move.
 */
export function dieOf(mover: 0 | 1, from: BgSpot, to: BgSpot): number | null {
  if (from === "bar") {
    if (typeof to !== "number") return null;
    // entryPoint(color, d) === to  ⇒  seat 0: d = 24 − to, seat 1: d = to + 1
    return mover === 0 ? 24 - to : to + 1;
  }
  if (typeof from !== "number") return null;
  if (to === "off") return pipToOff(mover, from);
  if (typeof to !== "number") return null;
  return Math.abs(to - from);
}

/**
 * The dice still playable after `staged` — one entry removed per sub-move, by the die it CONSUMED.
 *
 * ⚠️ THIS USED TO BE `dice.slice(staged.length)`, which drops the FIRST n dice whatever was played.
 * On a non-double played out of order that is exactly inverted: measured against the deployed
 * build, an opponent rolling [5,3] and playing the 3 first left the board reporting «[3] remaining»
 * — so the felt dimmed the 5 and kept the 3 bright for the whole ~950ms of each paced sub-move.
 * Part of the reported «while they moved, the dice did NOT dim as they were consumed».
 *
 * A BEAR-OFF may OVERSHOOT (a checker on pip 4 comes off on a 5 or a 6 when nothing is further
 * back), so its exact pip need not be among the dice: spend the smallest die that could have done
 * it, which is the same rule the engine used to offer the move.
 */
export function remainingAfter(
  dice: readonly number[],
  mover: 0 | 1,
  staged: readonly BgSub[],
): number[] {
  const left = [...dice];
  for (const s of staged) {
    const want = dieOf(mover, s.from, s.to);
    let i = want === null ? -1 : left.indexOf(want);
    if (i < 0 && want !== null) {
      for (let k = 0; k < left.length; k += 1) {
        if (left[k]! >= want && (i < 0 || left[k]! < left[i]!)) i = k;
      }
    }
    if (i < 0) i = 0; // unreadable sub-move: still spend one, so the count stays honest
    left.splice(i, 1);
  }
  return left;
}

/**
 * Apply one sub-move to a working board. Delegates to the ENGINE's `applySubMove` — which reads only
 * `from`/`to` — so hits and bar accounting match the engine exactly rather than being re-implemented.
 */
export function advance(board: Board, mover: 0 | 1, from: BgSpot, to: BgSpot): void {
  applySubMove(board, mover, { from, to, die: dieOf(mover, from, to) ?? 0 });
}

/**
 * Undo one sub-move on a working board, restoring a blot to `unhit` when the move had hit one.
 * (`applySubMove` has no inverse in the engine, and a plain reversal cannot know about the hit —
 * which is exactly why the undo EVENT carries `unhit`.)
 */
export function unapply(
  board: Board,
  mover: 0 | 1,
  from: BgSpot,
  to: BgSpot,
  unhit: number | null,
): void {
  const sign = mover === 0 ? 1 : -1;
  if (to === "off") board.off[mover] -= 1;
  else if (typeof to === "number") board.points[to] = (board.points[to] ?? 0) - sign;
  if (from === "bar") board.bar[mover] += 1;
  else if (typeof from === "number") board.points[from] = (board.points[from] ?? 0) + sign;
  if (unhit !== null) {
    board.points[unhit] = -sign; // the opponent's lone blot goes back
    board.bar[other(mover)] = Math.max(0, board.bar[other(mover)] - 1);
  }
}

/** Would landing on `to` knock a lone enemy blot to the bar? Returns the point, else null. */
function hitAt(board: Board, mover: 0 | 1, to: BgSpot): number | null {
  if (typeof to !== "number") return null;
  const opp = other(mover);
  return ownerAt(board.points, to) === opp && countAt(board.points, to) === 1 ? to : null;
}

/** The board is untouched AND nothing has been played: the true start of a game. */
function isFreshGame(view: BgView): boolean {
  if (
    view.lastMove.length > 0 ||
    view.staged.length > 0 ||
    view.off[0] !== 0 ||
    view.off[1] !== 0 ||
    view.bar[0] !== 0 ||
    view.bar[1] !== 0 ||
    view.dice.length === 0
  ) {
    return false;
  }
  // Positions must still be the 2-5-3-5 setup — a mid-game position that happens to have an empty
  // bar and nothing borne off must not trigger a ceremony.
  const setup = setupBoard().points;
  return setup.every((n, i) => n === (view.points[i] ?? 0));
}

function pushWalks(out: BgEvent[], board: Board, mover: 0 | 1, subs: readonly BgSub[]): void {
  for (const sub of subs) {
    const hit = hitAt(board, mover, sub.to);
    out.push({
      kind: "walk",
      sub: { from: sub.from, to: sub.to },
      mover,
      hit,
      bearOff: sub.to === "off",
    });
    advance(board, mover, sub.from, sub.to);
  }
}

/**
 * Did the OPPONENT gain a lone checker on `spot` between the two views? That is a blot coming back
 * off the bar — read from two AUTHORITATIVE views, so no reconstruction and no guessing. (Reversing
 * the rendered board cannot answer this: `unapply` has no way to know a hit happened.)
 */
function restoredBlotAt(
  rendered: BgView,
  incoming: BgView,
  mover: 0 | 1,
  spot: BgSpot,
): number | null {
  if (typeof spot !== "number") return null;
  const opp = other(mover);
  const had = ownerAt(rendered.points, spot) === opp;
  const has = ownerAt(incoming.points, spot) === opp && countAt(incoming.points, spot) === 1;
  return !had && has ? spot : null;
}

/**
 * A دست has just been WON by the side already on turn.
 *
 * ⚠️ THIS IS THE ONE CONFIRM THAT DOES NOT CHANGE HANDS. The engine deliberately leaves `turn` on
 * the winner (so the banner sits over the position that won it) while clearing `staged` — so the
 * same-turn branch below saw staged go N → 0 and read it as N UNDOS, the shape a player makes by
 * tapping «برگرداندن». It cannot tell a win from a take-back by the staged count, so it must not
 * try: measured on the deployed engine, 193 of 193 winning confirms replayed the winning turn
 * BACKWARDS, tray ۱۵ → ۱۴ → ۱۵.
 *
 * The discriminator is AUTHORITATIVE — the engine says a round (or the match) has been decided —
 * braced by the dice, which `confirm` clears and an `undo` never touches. `nextRound` refills them
 * (0 → N), so it cannot false-positive here either.
 */
function isWinningConfirm(rendered: BgView, incoming: BgView): boolean {
  const decided = incoming.roundResult !== undefined || incoming.status === "over";
  const confirmedInPlace = rendered.dice.length > 0 && incoming.dice.length === 0;
  return decided && confirmedInPlace && rendered.turn === incoming.turn;
}

/**
 * The ordered events that take the board from `rendered` to `incoming`.
 *
 * `rendered === null` is ADOPT-AT-MOUNT: a game already in progress yields NOTHING (a resume or a
 * reconnect must never replay a burst of motion), while a game at its very first position yields the
 * one-off opening ceremony.
 */
export function diffToEvents(rendered: BgView | null, incoming: BgView): BgEvent[] {
  const out: BgEvent[] = [];
  if (rendered === null) {
    if (incoming.opening !== undefined && isFreshGame(incoming)) {
      out.push({
        kind: "openingRollOff",
        dice: incoming.opening,
        winner: incoming.turn,
      });
    }
    return out;
  }

  // The board we are SHOWING — every rule question below is asked against this one, not the
  // incoming one, because that is the position the player is actually looking at.
  const board = boardFromView(rendered);
  const mover = seatOf(rendered.turn);

  /*
   * A FRESH دست (`nextRound`). The engine runs a NEW opening contest for every round — each one is
   * decided on its own roll — and the deciding pair rides the new view. The ceremony used to fire
   * only on ADOPT-AT-MOUNT, so from the second دست onward two contest dice simply appeared and the
   * player's first turn was a roll they had never thrown. `round` is the authoritative marker; the
   * whole board is new, so nothing else about the old one is worth replaying.
   *
   * Checked BEFORE the turn split: a new contest may or may not hand the deal to the same seat.
   */
  if (incoming.round > rendered.round) {
    if (incoming.opening !== undefined) {
      out.push({ kind: "openingRollOff", dice: incoming.opening, winner: incoming.turn });
    }
    return out;
  }

  /*
   * THE §5.1 CONTEST JUST RESOLVED — the second die has landed and the game has a starter.
   *
   * The contest itself is INTERACTIVE now (each player throws their own die, and the board shows
   * each one as it arrives), so there is nothing to replay while it runs — those frames diff to
   * nothing and the board simply shows them. What is worth a beat is the ANSWER: the ceremony
   * announces «شروع با تو / با حریف» over the two dice before play begins. The engine holds the
   * same beat (`OPENING_ANNOUNCE_MS`), so the bot cannot move under the announcement.
   */
  if (rendered.openingRoll !== undefined && incoming.openingRoll === undefined) {
    if (incoming.opening !== undefined) {
      out.push({ kind: "openingRollOff", dice: incoming.opening, winner: incoming.turn });
    }
    return out;
  }
  // …and while it is still running, nothing is replayed: the dice ARE the interaction.
  if (incoming.openingRoll !== undefined) return out;

  /*
   * A WON دست — replay whatever of the WINNING turn was never shown, and hand over nothing (the
   * turn did not move). Checked BEFORE the same-turn branch, whose staged-shrink clause would
   * otherwise read the cleared `staged` as a take-back. When the client saw every staging frame
   * this emits NOTHING, so the round banner starts at once and gets its whole ROUND_HOLD_MS.
   */
  if (isWinningConfirm(rendered, incoming)) {
    pushWalks(out, board, mover, incoming.lastMove.slice(rendered.staged.length));
    return out;
  }

  if (rendered.turn === incoming.turn) {
    if (rendered.dice.length === 0 && incoming.dice.length > 0) {
      out.push({
        kind: "roll",
        dice: incoming.dice,
        roller: incoming.turn,
        isDoubles: incoming.isDoubles,
      });
    }
    const shown = rendered.staged.length;
    const now = incoming.staged.length;
    if (now > shown) {
      pushWalks(out, board, mover, incoming.staged.slice(shown));
    } else if (now < shown) {
      // Undo pops from the END, newest first.
      for (let i = shown - 1; i >= now; i -= 1) {
        const sub = rendered.staged[i]!;
        out.push({
          kind: "undo",
          sub: { from: sub.from, to: sub.to },
          mover,
          unhit: restoredBlotAt(rendered, incoming, mover, sub.to),
        });
      }
    }
    if (incoming.noLegalMove && !rendered.noLegalMove) out.push({ kind: "dance", seat: mover });
  } else {
    // The previous mover confirmed: replay whatever of their turn we never showed, then hand over.
    const done = incoming.lastMove;
    const shown = rendered.staged.length;
    if (done.length > shown) pushWalks(out, board, mover, done.slice(shown));
    out.push({ kind: "handover", to: incoming.turn });
  }
  return out;
}

/** How long an event's motion runs, and how long to wait before releasing the NEXT one. */
export function eventTiming(e: BgEvent): { motionMs: number; holdMs: number } {
  switch (e.kind) {
    case "openingRollOff":
      return { motionMs: OPENING_MS, holdMs: OPENING_MS };
    case "roll":
      // The dice appear at once and animate; the HOLD is what stops the mover playing before the
      // numbers can be read.
      return { motionMs: THROW_MS, holdMs: THROW_MS + ROLL_HOLD_MS };
    case "walk": {
      const extra = (e.hit !== null ? HIT_MS : 0) + (e.bearOff ? BEAROFF_SETTLE_MS : 0);
      return { motionMs: WALK_MS + extra, holdMs: STEP_MS + extra };
    }
    case "undo":
      return { motionMs: WALK_MS, holdMs: STEP_MS };
    case "dance":
      return { motionMs: 0, holdMs: ROLL_HOLD_MS };
    case "handover":
      return { motionMs: HANDOVER_MS, holdMs: HANDOVER_MS };
  }
}

/** The hold, compressed once the backlog is pathological. `motionMs` is never touched. */
export function compressedHold(holdMs: number, backlog: number): number {
  if (backlog <= MAX_BACKLOG) return holdMs;
  return Math.max(COMPRESS_FLOOR_MS, Math.round(holdMs * COMPRESS_FACTOR));
}
